/**
 * Asset Assignments
 * Backend: POST /api/hr/assets/{id}/assign  (ASSET_ASSIGN)
 *          POST /api/hr/assets/{id}/return  (ASSET_ASSIGN)
 * No global assignments list endpoint — shows ASSIGNED assets + AVAILABLE assets.
 */
import { useState, useCallback } from "react";
import { PackageCheck, RotateCcw, History } from "lucide-react";
import { assetApi } from "../api";
import type { AssetCondition } from "../types";
import { useAsyncData, useMutation, usePagination } from "../hooks";
import {
  ActiveFilterBadges, AssetError, AssetLoading, AssetStatusBadge,
  BackBtn, Btn, Card, CardHeader, ConditionBadge, ExportBtn,
  FilterPanel, PageHeader, Pagination, SearchInput, SelectFilter,
  Table, TBody, TD, TH, THead, TR, fmtDate,
} from "../components/AssetUI";
import { downloadXls } from "../utils/export";

type Mode =
  | null
  | { type: "assign"; assetId: number; assetName: string }
  | { type: "return"; assetId: number; assetName: string };

const CONDITIONS: AssetCondition[] = ["EXCELLENT", "GOOD", "FAIR", "POOR", "DAMAGED"];

export default function AssetAssignmentsPage() {
  const pagination = usePagination();
  const [search, setSearch] = useState("");
  const [mode, setMode]     = useState<Mode>(null);

  const assigned = useAsyncData(
    () => assetApi.listAssets(pagination.page, pagination.size, { status: "ASSIGNED", search: search || undefined }),
    [pagination.page, pagination.size, search],
  );
  const availPagination = usePagination(0, 20);
  const available = useAsyncData(
    () => assetApi.listAssets(availPagination.page, availPagination.size, { status: "AVAILABLE" }),
    [availPagination.page, availPagination.size],
  );

  const activeBadges = [
    search && { label: `Search: ${search}`, onRemove: () => { setSearch(""); pagination.reset(); } },
  ].filter(Boolean) as { label: string; onRemove: () => void }[];

  const doExport = useCallback(() => {
    downloadXls(
      `assignments-${new Date().toISOString().slice(0, 10)}.xls`,
      "Asset Assignments",
      ["Asset No.", "Name", "Category", "Assigned To", "Status", "Condition"],
      (assigned.data?.content ?? []).map((a) => [
        a.assetNumber, a.name, a.categoryName,
        a.assignedEmployeeName ?? "—", a.status, a.condition,
      ]),
    );
  }, [assigned.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Asset Management"
        title="Assignments"
        actions={<BackBtn to="/assets" label="Dashboard" />}
      />

      {mode?.type === "assign" && (
        <AssignPanel assetId={mode.assetId} assetName={mode.assetName}
          onClose={() => setMode(null)}
          onDone={() => { setMode(null); assigned.reload(); available.reload(); }} />
      )}
      {mode?.type === "return" && (
        <ReturnPanel assetId={mode.assetId} assetName={mode.assetName}
          onClose={() => setMode(null)}
          onDone={() => { setMode(null); assigned.reload(); }} />
      )}

      {/* Available assets */}
      <Card noPad>
        <CardHeader title="Available — Ready to Assign" subtitle={`${available.data?.totalElements ?? 0} assets`} />
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <tr>
                {["Asset No.", "Name", "Category", "Location", "Condition", ""].map((h) => (
                  <th key={h} className="px-5 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {(available.data?.content ?? []).map((a) => (
                <tr key={a.id} className="hover:bg-slate-50/60">
                  <td className="px-5 py-3 font-mono text-xs font-semibold text-indigo-700">{a.assetNumber}</td>
                  <td className="px-5 py-3 font-semibold text-slate-800">{a.name}</td>
                  <td className="px-5 py-3 text-slate-400">{a.categoryName}</td>
                  <td className="px-5 py-3 text-slate-400">{a.locationName ?? "—"}</td>
                  <td className="px-5 py-3"><ConditionBadge value={a.condition} /></td>
                  <td className="px-5 py-3 text-right">
                    <Btn size="sm" variant="primary" icon={PackageCheck}
                      onClick={() => setMode({ type: "assign", assetId: a.id, assetName: a.name })}>
                      Assign
                    </Btn>
                  </td>
                </tr>
              ))}
              {!available.loading && !available.data?.content.length && (
                <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No available assets.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {available.data && available.data.totalPages > 1 && (
          <div className="border-t px-4 py-3">
            <Pagination page={available.data} onPageChange={availPagination.setPage} onSizeChange={availPagination.setSize} sizes={[10, 20, 50]} />
          </div>
        )}
      </Card>

      {/* Currently assigned */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput value={search} onChange={(v) => { setSearch(v); pagination.reset(); }}
            placeholder="Search asset or employee…" className="flex-1 min-w-60" />
          <FilterPanel activeCount={activeBadges.length} onRefresh={assigned.reload}>
            {/* server only supports search + status for asset list */}
          </FilterPanel>
          <ExportBtn onExport={doExport} />
        </div>
        <ActiveFilterBadges filters={activeBadges} />

        {assigned.loading && <AssetLoading label="Loading…" />}
        {assigned.error   && <AssetError message={assigned.error} onRetry={assigned.reload} />}

        {!assigned.loading && !assigned.error && (
          <Table>
            <THead>
              <TH>Asset No.</TH><TH>Name</TH><TH>Category</TH><TH>Condition</TH>
              <TH>Assigned To</TH><TH>Flagged</TH><TH></TH>
            </THead>
            <TBody>
              {(assigned.data?.content ?? []).map((a) => (
                <TR key={a.id}>
                  <TD><span className="font-mono text-xs font-semibold text-indigo-700">{a.assetNumber}</span></TD>
                  <TD><div className="font-semibold text-slate-800">{a.name}</div><div className="text-xs text-slate-400">{a.categoryName}</div></TD>
                  <TD muted>{a.categoryName}</TD>
                  <TD><ConditionBadge value={a.condition} /></TD>
                  <TD><span className="font-medium text-slate-700">{a.assignedEmployeeName ?? "—"}</span></TD>
                  <TD>{a.flaggedForDisposal ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600">Yes</span> : <span className="text-slate-300">—</span>}</TD>
                  <TD>
                    <div className="flex gap-2 justify-end">
                      <Btn size="sm" variant="dangerOutline" icon={RotateCcw}
                        onClick={() => setMode({ type: "return", assetId: a.id, assetName: a.name })}>
                        Return
                      </Btn>
                    </div>
                  </TD>
                </TR>
              ))}
              {!assigned.data?.content.length && (
                <TR><TD className="py-8 text-center text-slate-400">No assigned assets found.</TD></TR>
              )}
            </TBody>
          </Table>
        )}
        {assigned.data && (
          <Pagination page={assigned.data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        )}
      </div>
    </div>
  );
}

// ── Assign panel ───────────────────────────────────────────────────────────────

function AssignPanel({ assetId, assetName, onClose, onDone }: {
  assetId: number; assetName: string; onClose: () => void; onDone: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [employeeId, setEmployeeId] = useState("");
  const [assignedBy, setAssignedBy] = useState("");
  const [assignedDate, setDate]     = useState(today);
  const [condition, setCondition]   = useState<AssetCondition>("GOOD");
  const [purpose, setPurpose]       = useState("");
  const [notes, setNotes]           = useState("");
  const assign = useMutation((p: Parameters<typeof assetApi.assignAsset>[1]) => assetApi.assignAsset(assetId, p));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employeeId) return;
    await assign.mutate({ employeeId: Number(employeeId), assignedDate, assignedBy: assignedBy || undefined, conditionAtAssignment: condition, purpose: purpose || undefined, notes: notes || undefined });
    onDone();
  }
  return (
    <Card className="border-indigo-200 bg-indigo-50/30">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Assign: {assetName}</h3>
          <button type="button" onClick={onClose} className="text-xs text-slate-400 hover:text-slate-600">✕ Cancel</button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Employee ID *
            <input required type="number" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" placeholder="e.g. 42" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Assigned By
            <input type="text" value={assignedBy} onChange={(e) => setAssignedBy(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" placeholder="Your name" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Assignment Date *
            <input required type="date" value={assignedDate} onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" />
          </label>
          <div className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Condition
            <SelectFilter label="Condition" value={condition} onChange={(v) => setCondition(v as AssetCondition)}
              options={CONDITIONS.map((c) => ({ label: c, value: c }))} />
          </div>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 sm:col-span-2">
            Purpose
            <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" placeholder="e.g. Primary work laptop" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 sm:col-span-2">
            Notes
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" />
          </label>
        </div>
        {assign.error && <p className="text-sm text-red-600">{assign.error}</p>}
        <div className="flex gap-2">
          <Btn type="submit" variant="primary" loading={assign.loading} loadingText="Assigning…">Confirm Assignment</Btn>
          <Btn type="button" onClick={onClose}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}

// ── Return panel ───────────────────────────────────────────────────────────────

function ReturnPanel({ assetId, assetName, onClose, onDone }: {
  assetId: number; assetName: string; onClose: () => void; onDone: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [returnedDate, setDate] = useState(today);
  const [receivedBy, setReceivedBy] = useState("");
  const [condition, setCondition]   = useState<AssetCondition>("GOOD");
  const [notes, setNotes]           = useState("");
  const ret = useMutation((p: Parameters<typeof assetApi.returnAsset>[1]) => assetApi.returnAsset(assetId, p));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await ret.mutate({ returnedDate, receivedBy: receivedBy || undefined, conditionAtReturn: condition, notes: notes || undefined });
    onDone();
  }
  return (
    <Card className="border-orange-200 bg-orange-50/30">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-800">Return: {assetName}</h3>
          <button type="button" onClick={onClose} className="text-xs text-slate-400 hover:text-slate-600">✕ Cancel</button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Return Date *
            <input required type="date" value={returnedDate} onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Received By
            <input type="text" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" placeholder="Your name" />
          </label>
          <div className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Condition at Return
            <SelectFilter label="Condition" value={condition} onChange={(v) => setCondition(v as AssetCondition)}
              options={CONDITIONS.map((c) => ({ label: c, value: c }))} />
          </div>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Notes
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm font-normal outline-none focus:border-indigo-500" placeholder="Damage notes" />
          </label>
        </div>
        {ret.error && <p className="text-sm text-red-600">{ret.error}</p>}
        <div className="flex gap-2">
          <Btn type="submit" variant="emerald" loading={ret.loading} loadingText="Processing…">Confirm Return</Btn>
          <Btn type="button" onClick={onClose}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}
